import {Inject, Injectable} from '@angular/core';
import {RequestsService} from "./requests.service";
import { first, map, take } from 'rxjs/operators'
import {CommonUtils} from "../utils/common.utils";
import { GlobalConstants } from 'app/common/global-constants';
import {
  accessControlResourceMapper,
  namespace,
  personActorType,
  RESOURCE_NAME_SEPARATOR,
  ResourceTypeOperations,
  resourceType,
} from "../common/manage-permission";
import {
  uiPageTypeOperationGroupName,
  userConsentEnum,
  viewLabelValues,
} from "../common/common-constants";
import { forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  get help_messages(): {} {
    return this._help_messages;
  }

  get updateOverridesTypeOptions(): string[] {
    return this._updateOverridesTypeOptions;
  }

  get transitTimeOptionsForOnboarding(): string[] {
    return this._transitTimeOptionsForOnboarding;
  }

  get numberOfDaysOfHistoryOptions(): number[] {
    return this._numberOfDaysOfHistoryOptions;
  }

  get patternOptions(): string[] {
    return this._patternOptions;
  }

  get padTransitTimeOptions(): string[] {
    return this._padTransitTimeOptions;
  }

  get transitTimeValueOptions(): string[] {
    return this._transitTimeValueOptions;
  }

  get businessUnitOptions(): string[] {
    return this._businessUnitOptions;
  }

  get fromCountryCodeOptions(): string[] {
    return this._fromCountryCodeOptions;
  }

  get componentKeyPlaceholderOptions(): string[] {
    return this._componentKeyPlaceholderOptions;
  }

  get lookUpKeysOptions(): string[] {
    return this._lookUpKeysOptions;
  }

  get transitTimeOptions(): string[] {
    return this._transitTimeOptions;
  }

  get passportEnabledShipmethods(): object{
    return this._passportEnabledShipmethods;
  }

  get getUploadTypeOptions(): object{
    return this._uploadTypeOptions;
  }

  get getRemoveTransitTimeLanesFileTypeOptions(): object{
    return this._removeTransitTimeLanesFileTypeOptions;
  }

  // TODO: Options can change based on requirement
  private _businessUnitOptions = ['STANDARD', 'SBR1', 'MFN_INDIA', 'OOC_STANDARD', 'OOC_SBR1', 'DOM_STT', 'OOC_STT'];
  private _fromCountryCodeOptions = ['US', 'JP', 'IN', 'AU', 'GB', 'IT', 'DE', 'ES', 'CN', 'MX'];
  private _componentKeyPlaceholderOptions = ['CARRIER_NAME:SHIP_METHOD', 'VIRTUAL_CARRIER'];
  private _lookUpKeysOptions = ['MERCHANT_ID', 'FROM_COUNTRY_CODE', 'TO_COUNTRY_CODE', 'FROM_ZIP_CODE', 'TO_ZIP_CODE', 'TO_AREA'];
  private _transitTimeOptions = ['CARRIER_PROVIDED', 'COMPUTED', 'ML_OPTIMIZED', 'PRECISION_OPTIMIZED', 'HYBRID_TRANSIT_TIME_TYPE'];
  private _updateOverridesTypeOptions = ['Add Overrides', 'Remove Overrides'];
  private _transitTimeOptionsForOnboarding = ['CARRIER_PROVIDED'];
  private _padTransitTimeOptions = ['-144', '-120', '-96', '-72', '-48', '-24', '24', '48', '72', '96', '120', '144'];
  private _transitTimeValueOptions = ['ALL', '24', '48', '72', '96', '120', '144', '168'];
  private _patternOptions = ['', 'ZIP-2', 'ZIP-3', 'ZIP-5', 'ZIP-BEFORE-SPACE'];
  private _patternToRegexMap = {
    'ZIP-2': '^[a-zA-Z0-9]{2}',
    'ZIP-3': '^[a-zA-Z0-9]{3}',
    'ZIP-5': '^[a-zA-Z0-9]{5}',
    'ZIP-BEFORE-SPACE': '^([^\s]+)'
  };
  private _help_messages = {
    'from_country_code': 'Select from_country_code. For more information, please refer to help tab.',
    'business_unit': 'The business unit is group of parties and services grouped together based on levels which they operate. For more information, please refer to help tab.',
    'level_of_data': 'The granularity at which the transit time file is defined. For more information, please refer to help tab.',
    'look_up_keys': 'These are columns in transit time file. For more information, please refer to help tab.',
    'look_up_key': 'Select column name from the drop down. For more information, please refer to help tab.',
    'transit_time_type': 'The transit time type of the file. For more information, please refer to help tab.',
    'pattern': 'An optional pattern to query only on part of the ZIP code. For more information, please refer to help tab.',
    'file': 'Upload transit time file. For more information, please refer to help tab.',
    'current_transit_time': 'This shows the current transit time being published. For more information, please refer to help tab.',
    'history': 'Select number of days of history. For more information, please refer to help tab.',
    'number_of_hours_to_pad': 'Number of hours to pad all the lanes of transit time file. For more information, please refer to help tab.',
    'expected_schema': 'Expected schema of the transit time file. For more information, please refer to help tab.',
    's3_file_path': 'S3 location of the file uploaded by business in the business S3.',
    'requested_approver': 'You can\'t add yourself as an approver. Only yourself and approvers mentioned can approve the request. Only lower case letters and \';\' are allowed. For more information, please refer to help tab.',
    'transit_time': 'Transit Time to which the threshold will be applied, select ALL to apply on all the lanes',
    'threshold' : 'For any transit time update request, delta(percentage change) is calculated between uploaded file & latest production, threshold is a maximum percentage-change/delta between two files such that update request is Auto Approved',
    'cron_value' : 'Rule to dynamically change threshold based on certain days/date',
    'baseThreshold' : 'Base Threshold are non-editable threshold, these are used when for a given transit time no custom threshold are configured, to change this email at msti-tech@amazon.com',
    'transit_time_upload_type': 'It represents the upload type of transit time. For more information, please refer to help tab.',
    'remove_transit_time_lanes_file_type': 'It represents the file type required to remove transit time lanes. For more information, please refer to help tab.',
  };
  private _uploadTypeOptions = ['UI_UPLOAD', 'S3_UPLOAD'];
  private _removeTransitTimeLanesFileTypeOptions = ['COMPLETE_FILE', 'REMOVE_LANES_FILE'];
  private _passportEnabledShipmethods = {};
  public resourcePermission: { admin: boolean; superUser: boolean } = {
    admin: false,
    superUser: false,
  };
  public pageId: string = '';


  constructor(@Inject('RequestsService') private requestsService,
  @Inject('CommonUtils') private readonly commonUtils: CommonUtils,) {
  }

  getConfigurationRecordsBasedOnFromCountryCode(fromCountryCode) {
    let payload = {
      'fromCountryCode': fromCountryCode
    };
    this.getAllPassportEnabledShipmethods();
    return this.requestsService.put('/fetch-all-transit-time-configuration', payload)
  }

  getAllPassportEnabledShipmethods() {
    this.requestsService
      .put("/get-all-passport-enabled-ship-methods", {})
      .pipe(first())
      .subscribe((result) => {
        if (result && this.commonUtils.isObject(result?.passportEnabledShipMethodsMap)) {
          const shipmethodsMapList: [][] = Object.values(result?.passportEnabledShipMethodsMap);
          const shipmethodsList = shipmethodsMapList.reduce(
            (list, val) => list.concat(val),
            []
          );
          this._passportEnabledShipmethods = shipmethodsList.reduce(
            (shipmethodsMapper, shipmethod) => {
              shipmethodsMapper[shipmethod] = true;
              return shipmethodsMapper;
            },
            {}
          );
        }
      });
  }

  getBindlePermission(userAlias, id) {
    const resource = accessControlResourceMapper[id];
    const operationMapper = {};
    const actor = {
      actorType: personActorType,
      actorId: userAlias,
    };
    resource.operations.forEach((operation) => {
      const requestBody = {
        actor,
        operation,
        resource: {
          namespace,
          resourceType: resource.type,
          resourceName: resource.name,
        },
      };

      operationMapper[operation] = this.requestsService
        .put(GlobalConstants.GET_RESOURCE_PERMISSION_ENDPOINT, requestBody)
        .pipe(
          map((response: any) => response?.authorized),
          take(1)
        );
    });

    return forkJoin(operationMapper);
  }

  private formatByResourceNameSeperator(...resourceNameList) {
    return resourceNameList.reduce(
      (accumulator, resourceName) =>
        `${accumulator}${RESOURCE_NAME_SEPARATOR}${resourceName}`,
    );
  }

  private buildPermissionObservable(
    actor,
    operation: string,
    resourceType: string,
    resourceName: string,
  ) {
    const requestBody = {
      actor,
      operation,
      resource: {
        namespace,
        resourceType,
        resourceName,
      },
    };
    return this.requestsService
      .put(GlobalConstants.GET_RESOURCE_PERMISSION_ENDPOINT, requestBody)
      .pipe(
        map((response: any) => response?.authorized),
        take(1),
      );
  }

  /**
 * Checks whether the currently logged-in user has permission to perform the action
 *
 * Permission Model Overview:
 * --------------------------
 * 1. **Admin / SuperUser shortcut**: If the user is already known to be an
 *    admin or superUser, they are granted permission immediately without any 
 *    further HTTP calls.
 *
 * 2. **Onboarding page** (`onboardingRequestId`):
 *    - The resource is a BusinessUnit resource.
 *    - Resource name is built as: `<fromCountryCode>:<businessUnit>`
 *    - Operation: `canEdit_onboardingRequestId`
 *
 * 3. **All other pages** (update, configure, overrides, threshold, approvals, etc.):
 *    - The resource is a ComponentKeyValue resource.
 *    - Multiple resource names are built based on the depth of `componentKeyValue`:
 *      a. Always includes a wildcard: `<fromCountryCode>:<businessUnit>:ALL`
 *      b. If componentKeyValue has 1 part (e.g. "CARRIER_A"):
 *         adds `<fromCountryCode>:<businessUnit>:CARRIER_A`
 *      c. If componentKeyValue has 2 parts (e.g. "CARRIER_A:SHIP_METHOD_1"):
 *         adds `<fromCountryCode>:<businessUnit>:CARRIER_A:ALL`
 *         and `<fromCountryCode>:<businessUnit>:CARRIER_A:SHIP_METHOD_1`
 *    - Operation is looked up from `uiPageTypeOperationGroupName` by pageId,
 *      combined with transitTimeType: e.g. `can_edit_CARRIER_PROVIDED`
 *    - If ANY of the resource name checks returns `authorized: true`, access is granted.
 *
 * @param requestObj - The request payload containing user and resource context fields.
 * @returns A Promise resolving to `true` if the user has permission, `false` otherwise.
 *          Also shows a toast error notification if access is denied.
 */
  checkUserPermissions(requestObj): Promise<boolean> {

    if (this.resourcePermission?.admin || this.resourcePermission?.superUser) {
      return Promise.resolve(true);
    }

    const {
      createdBy,
      fromCountryCode,
      businessUnit,
      componentKeyValue,
      transitTimeType,
    } = requestObj;

    const actor = {
      actorType: personActorType,
      actorId: createdBy,
    };

    const resourcePermissionsMapper = {};

    if (this.pageId === viewLabelValues.onboardingRequestId) {
      const operation = `${ResourceTypeOperations.canEdit}_${viewLabelValues.onboardingRequestId}`;

      const resourceNames = [
        this.formatByResourceNameSeperator(fromCountryCode, businessUnit),
      ];

      resourceNames.forEach((resourceName) => {
        resourcePermissionsMapper[resourceName] =
          this.buildPermissionObservable(
            actor,
            operation,
            resourceType.businessUnitResourceType,
            resourceName,
          );
      });
    } else {
      const componentKeyValueList =
        componentKeyValue?.split(
          GlobalConstants.COMPONENT_KEY_VALUE_SEPARATOR,
        ) ?? [];
      const resourceNames = [
        this.formatByResourceNameSeperator(
          fromCountryCode,
          businessUnit,
          userConsentEnum.ALL,
        ),
      ];

      if (componentKeyValueList?.length === 1)
        resourceNames.push(
          this.formatByResourceNameSeperator(
            fromCountryCode,
            businessUnit,
            componentKeyValueList[0],
          ),
        );

      if (componentKeyValueList?.length === 2)
        resourceNames.push(
          this.formatByResourceNameSeperator(
            fromCountryCode,
            businessUnit,
            componentKeyValueList[0],
            userConsentEnum.ALL,
          ),
          this.formatByResourceNameSeperator(
            fromCountryCode,
            businessUnit,
            componentKeyValueList[0],
            componentKeyValueList[1],
          ),
        );

      const operationGroup = uiPageTypeOperationGroupName[this.pageId];
      if (!operationGroup) {
        console.error(`${GlobalConstants.NO_OPERATIONS_FOUND} ${this.pageId}`);
        return Promise.resolve(false);
      }
      const operation = `${operationGroup}_${transitTimeType}`;

      resourceNames.forEach((resourceName) => {
        resourcePermissionsMapper[resourceName] =
          this.buildPermissionObservable(
            actor,
            operation,
            resourceType.ComponentKeyValueResourceType,
            resourceName,
          );
      });
    }

    return forkJoin(resourcePermissionsMapper)
      .pipe(
        map((resourcePermissionsList: any) => {
          const permissions = Object.values(resourcePermissionsList);
          const hasPermission = permissions.includes(true);
          return hasPermission; // ← boolean returned from map, becomes Promise value
        }),
      )
      .toPromise();
  }

  getRegexFromPattern(pattern) {
    return this._patternToRegexMap[pattern];
  }

  getPatternFromRegex(regex) {
    let reverseMap = {}
    for (let key in this._patternToRegexMap) {
      reverseMap[this._patternToRegexMap[key]] = key;
    }
    return reverseMap[regex]
  }

  private _numberOfDaysOfHistoryOptions = [7, 14, 30, 60, 120]


}
